import moment from "moment-timezone";
import { Button, Box, Modal, Paper, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { makeStyles } from '@material-ui/core/styles';
import productPhoto from "../../../assets/images/productphoto.png"
import { useContext, useEffect, useState } from "react";
import ChatAPIs from "../../../apis/chat.apis";
import UserAPIs from "../../../apis/user.apis";
import Avatar1 from "../../../assets/images/Modal Images/6673.jpg"
import Avatar2 from "../../../assets/images/Modal Images/Layer 32.jpg"
import Avatar3 from "../../../assets/images/Modal Images/Layer 331.jpg"
import Avatar4 from "../../../assets/images/Modal Images/Layer 34.jpg"
import { AppStateContext } from "../../../App";

export const CurrentDate = () => (
  <span
    style={{
      fontSize: 10,
      color: "#6b7280",
      fontWeight: 500,
      letterSpacing: "0.02em",
      lineHeight: 1.2,
      textTransform: "uppercase",
    }}
  >
    {moment().format("DD MMM YYYY")}
  </span>
);

interface ButtonControlProps {
  onYesClick?: () => void;
  onNoClick: () => void;
  yesLabel?: string;
  noLabel: string;
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalPaper: {
    padding: theme.spacing(3),
    width: 300,
    textAlign: 'center',
  },
  modalButtons: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

export const ButtonControl: React.FC<ButtonControlProps> = ({
  onYesClick,
  onNoClick,
  yesLabel,
  noLabel,
}) => {
  const { t } = useTranslation("dashboard");
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const handleNoClick = () => {
    setOpen(true);
  };

  const handleConfirm = () => {
    setOpen(false);
    onNoClick();
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <>
      {onYesClick && yesLabel && (
        <Button
          variant="outlined"
          fullWidth
          style={{
            paddingTop: 12,
            paddingBottom: 12,
            marginBottom: 16,
            fontWeight: 600,
            color: "#6500ff",
            background: "transparent",
            borderRadius: "2px",
          }}
          onClick={onYesClick}
        >
          {yesLabel}
        </Button>
      )}
      <Button
        variant="outlined"
        fullWidth
        style={{
          paddingTop: 12,
          paddingBottom: 12,
          marginBottom: 16,
          fontWeight: 500,
          color: "#222",
          borderColor: "#ddd",
          borderRadius: "2px",
        }}
        onClick={handleNoClick}
      >
        {noLabel}
      </Button>
      <Modal
        open={open}
        onClose={handleCancel}
        className={classes.modal}
      >
        <Paper className={classes.modalPaper}>
          <Typography variant="h6" gutterBottom>
            {t("onboarding.common.confirmTitle")}
          </Typography>
          <div className={classes.modalButtons}>
            <Button
              variant="outlined"
              style={{ backgroundColor: '#fff', color: '#000', borderRadius: '0px' }}
              onClick={handleConfirm}
            >
              {t("onboarding.common.confirmYes")}
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: '#000000', color: '#fff', borderRadius: '0px' }}
              onClick={handleCancel}
            >
              {t("onboarding.common.confirmCancel")}
            </Button>
          </div>
        </Paper>
      </Modal>
    </>
  );
};

// ---- In-memory caches ----
let cachedProductUrl: string | null | undefined = undefined;
let cachedLogoUrl: string | null | undefined = undefined;
let cachedObjectFitStyle: "cover" | "contain" | undefined = undefined;

export const clearProductCache = () => {
  cachedProductUrl = undefined;
  cachedLogoUrl = undefined;
  cachedObjectFitStyle = undefined;
};

export const ProductImage: React.FC = () => {
  const { t } = useTranslation("dashboard");
  const [imageUrl, setImageUrl] = useState<string | null | undefined>(cachedProductUrl);
  const [objectFitStyle, setObjectFitStyle] = useState<"cover" | "contain" | undefined>(cachedObjectFitStyle);
  
  useEffect(() => {
    if (cachedProductUrl !== undefined && cachedObjectFitStyle !== undefined) {
      return;
    }

    let mounted = true;

    const chatApi = new ChatAPIs();
    chatApi.get_products("1")
      .then((res) => {
        const url = res?.items?.[0]?.image_url;
        cachedProductUrl = url ? url : null;

        if (url) {
          const img = new Image();
          img.onload = () => {
            const aspectRatio = img.naturalWidth / img.naturalHeight;
            const newObjectFit = aspectRatio > 1.25 ? "cover" : "contain";
            cachedObjectFitStyle = newObjectFit;
            if (mounted) {
              setObjectFitStyle(newObjectFit);
              setImageUrl(url);
            }
          };
          img.onerror = () => {
            cachedProductUrl = null;
            cachedObjectFitStyle = "contain";
            if (mounted) {
              setImageUrl(null);
              setObjectFitStyle("contain");
            }
          };
          img.src = url;
        } else {
          cachedProductUrl = null;
          cachedObjectFitStyle = "contain";
          if (mounted) setObjectFitStyle("contain");
        }
      })
      .catch(() => {
        cachedProductUrl = null;
        cachedObjectFitStyle = "contain";
        if (mounted) {
          setImageUrl(null);
          setObjectFitStyle("contain");
        }
      });

    return () => { mounted = false; };
  }, []);

  // if (imageUrl === undefined || objectFitStyle === undefined) {
  //   return null; // 👈 nothing while loading (no fallback flash)
  // }
  
  return (
    imageUrl != null ? <img
      src={imageUrl}
      alt={t("onboarding.common.productAlt")}
      style={{
        borderRadius: "8px",
        width: "148px",
        height: "177px",
        marginTop: 8,
        objectFit: objectFitStyle,
      }}
    /> :
    <img
      src={productPhoto}
      alt={t("onboarding.common.productAlt")}
      style={{
        borderRadius: "8px",
        width: "148px",
        height: "177px",
        marginTop: 8,
        objectFit: "contain",
      }}
    />

  );
};

export const LogoImage: React.FC = () => {
  const { t } = useTranslation("dashboard");
  const { shop } = useContext(AppStateContext);
  const [imageUrl, setImageUrl] = useState<string | null | undefined>(cachedLogoUrl);

  useEffect(() => {
    if (cachedLogoUrl !== undefined) {
      return; // already cached, don't fetch again
    }

    const userApi = new UserAPIs();
    userApi.fetch_user_signature()
      .then((res) => {
        const url = res?.signature?.logo_url;
        cachedLogoUrl = url ? url : null; // cache only after response
        setImageUrl(cachedLogoUrl);
      })
      .catch((err) => {
        console.error("Error fetching signature:", err);
        cachedLogoUrl = null;
        setImageUrl(null);
      });
  }, []);

  if (imageUrl === undefined) {
    return null; // 👈 nothing until resolved
  }

  return (
    <Box
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <Box
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "2px solid #ffffff",
          boxShadow: "0 0 0 1px rgba(17,24,39,0.14)",
          overflow: "hidden",
          backgroundColor: "#ffffff",
          flexShrink: 0,
        }}
      >
        <img
          src={imageUrl || "https://cdn.worldvectorlogo.com/logos/random.svg"}
          alt={t("onboarding.common.logoAlt")}
          width={36}
          height={36}
          style={{
            objectFit: "cover",
            display: "block",
          }}
        />
      </Box>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#0f172a",
          maxWidth: 116,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          letterSpacing: "0.01em",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {shop?.name || t("onboarding.common.brandFallback")}
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#3b82f6",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
            boxShadow: "0 0 0 1px rgba(255,255,255,0.9)",
          }}
        >
          ✓
        </span>
      </span>
    </Box>
  );
};

interface AvatarImagesProps {
  className?: string;
}

export const AvatarImages: React.FC<AvatarImagesProps> = ({ className }) => {
  let arr = [Avatar4, Avatar3, Avatar2, Avatar1]
  return (
    <Box
      sx={{ display: "flex", mb: 2 }}
      className={className}
    >
      {arr.map((i) => (
        <img
          key={i}
          src={i}
          alt="avatar"
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            marginRight: -24,
            border: "5px solid #fff",
          }}
        />
      ))}
    </Box>
  );
};